/**
 * Boost Commerce Cart Yardage Display Fix v2.0
 * Converts unit quantities to yard display with queue-based quantity adjustments
 *
 * Implements request queue system to prevent rate limiting and enable
 * snappy quantity adjustments directly in the Boost cart drawer.
 *
 * Key features:
 * - Optimistic UI updates (instant feedback)
 * - Request queue with merging (10 rapid clicks = 1 API call)
 * - Throttling (100ms between requests, max 10 req/sec)
 * - Cart caching (500ms window to reduce duplicate fetches)
 * - Retry logic for failed requests
 * - Error recovery with display revert
 * - ENABLES +/- BUTTONS IN BOOST CART
 */

(function() {
  'use strict';

  console.log('[Boost Yardage] VERSION 2.1 - double-wrapper race condition fix loaded');

  const CONFIG = {
    yardsToUnitsMultiplier: 2, // 1 unit = 0.5 yards
    maxRetries: 2, // Max retry attempts for failed requests
    requestThrottle: 100, // Min ms between API requests (10 req/sec)
    cartCacheDuration: 500, // Cache cart.js fetch for 500ms
    tagCacheTTL: 60 * 60 * 1000, // 1 hour tag cache
  };

  // Cache for product tags (handle → {isFractional, timestamp})
  const productTagCache = new Map();
  const pendingTagFetches = new Map(); // Deduplicate simultaneous fetches

  /**
   * Check if a product has the "Fractional Yardage" tag
   * @param {string} handle - Product handle
   * @param {object} cartItem - Optional cart item for fallback checking
   * @returns {Promise<boolean>}
   */
  async function checkProductTags(handle, cartItem = null) {
    const now = Date.now();

    // Check cache first
    if (productTagCache.has(handle)) {
      const cached = productTagCache.get(handle);
      if (now - cached.timestamp < CONFIG.tagCacheTTL) {
        console.log('[Boost Yardage] Tag cache HIT:', handle, '→', cached.isFractional);
        return cached.isFractional;
      }
      // Expired, remove
      productTagCache.delete(handle);
    }

    // Check if already fetching (deduplicate)
    if (pendingTagFetches.has(handle)) {
      console.log('[Boost Yardage] Awaiting pending fetch for', handle);
      return await pendingTagFetches.get(handle);
    }

    // Fetch product data
    const fetchPromise = (async () => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();

        const isFractional = product.tags.some(tag =>
          tag.toLowerCase() === 'fractional yardage'
        );

        // Cache result
        productTagCache.set(handle, {isFractional, timestamp: now});
        console.log('[Boost Yardage] Checked tags for', handle, '→', isFractional ? 'FRACTIONAL' : 'NOT fractional');

        return isFractional;
      } catch (error) {
        console.error('[Boost Yardage] Error checking tags for', handle, error);

        // Fallback: check if item already has _is_fractional property
        if (cartItem && cartItem.properties && cartItem.properties._is_fractional === 'true') {
          console.log('[Boost Yardage] Using _is_fractional fallback for', handle);
          return true; // Trust existing property
        }

        return false; // Default to non-fractional on error
      } finally {
        pendingTagFetches.delete(handle);
      }
    })();

    pendingTagFetches.set(handle, fetchPromise);
    return await fetchPromise;
  }

  /**
   * CartRequestQueue
   * Handles queuing, merging, and throttling of cart update requests
   */
  class CartRequestQueue {
    constructor() {
      this.queue = new Map(); // variantId → {targetQty, element, retries}
      this.processing = false;
      this.cartCache = null;
      this.cartCacheExpiry = 0;
    }

    /**
     * Add or merge request into queue
     */
    enqueue(variantId, targetQty, element) {
      if (this.queue.has(variantId)) {
        // Merge: update target quantity
        this.queue.get(variantId).targetQty = targetQty;
        console.log('[Queue] Merged request for variant', variantId, 'new target:', targetQty);
      } else {
        this.queue.set(variantId, {targetQty, element, retries: 0});
        console.log('[Queue] Added variant', variantId, 'target:', targetQty);
      }

      this.startProcessor();
    }

    /**
     * Process queue sequentially with throttling
     */
    async startProcessor() {
      if (this.processing) return;
      this.processing = true;

      while (this.queue.size > 0) {
        const [variantId, item] = this.queue.entries().next().value;
        this.queue.delete(variantId);

        try {
          await this.processItem(variantId, item);
        } catch (error) {
          console.error('[Queue] Failed to process:', error);
          this.handleFailure(variantId, item, error);
        }

        // Throttle: 100ms between requests (10 req/sec max)
        await this.delay(CONFIG.requestThrottle);
      }

      this.processing = false;
      console.log('[Queue] Processing complete');
    }

    /**
     * Process single item
     */
    async processItem(variantId, item) {
      // Use cached cart if fresh (<500ms old)
      if (!this.cartCache || Date.now() > this.cartCacheExpiry) {
        const response = await fetch('/cart.js');
        this.cartCache = await response.json();
        this.cartCacheExpiry = Date.now() + CONFIG.cartCacheDuration;
        console.log('[Queue] Fetched fresh cart data');
      }

      // Find item by variant ID and get its line index
      const cartItemIndex = this.cartCache.items.findIndex(i =>
        String(i.variant_id) === String(variantId)
      );

      if (cartItemIndex === -1) {
        throw new Error(`Variant ${variantId} not found in cart`);
      }

      const cartItem = this.cartCache.items[cartItemIndex];
      const lineIndex = cartItemIndex + 1; // Shopify uses 1-based indexing

      // CRITICAL: Verify product actually has "Fractional Yardage" tag
      const productHandle = cartItem.handle;
      const isFractional = await checkProductTags(productHandle, cartItem);

      if (!isFractional) {
        console.warn('[Queue] ⚠️ Product', productHandle, 'does NOT have Fractional Yardage tag. Preserving existing properties.');

        // CRITICAL: Preserve existing properties (gift messages, custom engraving, etc.)
        // Only remove Yardage and _is_fractional, keep everything else
        const existingProperties = cartItem.properties || {};
        const cleanedProperties = {};

        for (const key in existingProperties) {
          if (key !== 'Yardage' && key !== '_is_fractional') {
            cleanedProperties[key] = existingProperties[key];
          }
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            line: lineIndex,
            quantity: item.targetQty,
            properties: cleanedProperties // Keep existing properties, remove fractional ones
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          if (errorText.includes('inventory') || errorText.includes('available') || response.status === 422) {
            const error = new Error('Insufficient inventory available');
            error.isInventoryError = true;
            throw error;
          }
          throw new Error(`API error ${response.status}: ${errorText}`);
        }

        const updatedCart = await response.json();
        this.updateBoostCartTotals(updatedCart);
        this.cartCache = null;
        return; // Exit early - this item is not fractional
      }

      // Product IS fractional - calculate yardage and set properties
      const yards = item.targetQty / CONFIG.yardsToUnitsMultiplier;
      const yardageText = `${yards} ${yards === 1 ? 'yard' : 'yards'}`;

      console.log('[Queue] ✓ Product', productHandle, 'IS fractional. Setting yardage:', yardageText);

      // Single atomic API call
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          line: lineIndex,
          quantity: item.targetQty,
          properties: {
            'Yardage': yardageText,
            '_is_fractional': 'true'
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();

        // Check for inventory error
        if (errorText.includes('inventory') || errorText.includes('available') || response.status === 422) {
          const error = new Error('Insufficient inventory available');
          error.isInventoryError = true;
          throw error;
        }

        throw new Error(`API error ${response.status}: ${errorText}`);
      }

      const updatedCart = await response.json();
      console.log('[Queue] ✓ Updated line', lineIndex, 'variant', variantId, 'to', yardageText);

      // Update Boost cart totals
      this.updateBoostCartTotals(updatedCart);

      // Invalidate cache after successful update
      this.cartCache = null;
    }

    /**
     * Handle failures with retry logic
     */
    handleFailure(variantId, item, error) {
      if (error.isInventoryError) {
        // Don't retry inventory errors
        this.revertYardageDisplay(item.element);
        this.showErrorMessage(item.element, 'Not enough inventory available');
        console.error('[Queue] Inventory error for variant', variantId, error);
      } else if (item.retries < CONFIG.maxRetries) {
        // Retry for network errors
        item.retries++;
        this.queue.set(variantId, item);
        console.log('[Queue] Retrying variant', variantId, `(attempt ${item.retries + 1}/${CONFIG.maxRetries + 1})`);
      } else {
        // Final failure: revert display
        this.revertYardageDisplay(item.element);
        this.showErrorMessage(item.element, 'Failed to update quantity');
        console.error('[Queue] Final failure for variant', variantId, error);
      }
    }

    /**
     * Revert display to previous value
     */
    revertYardageDisplay(yardsDisplayElement) {
      const previousUnits = parseInt(yardsDisplayElement.getAttribute('data-previous-units')) || 0;
      if (previousUnits > 0) {
        const yards = previousUnits / CONFIG.yardsToUnitsMultiplier;
        const yardText = yards === 1 ? 'yard' : 'yards';

        // Update the number
        yardsDisplayElement.textContent = yards;

        // Update the label
        const labelElement = yardsDisplayElement.parentElement.querySelector('.boost-yards-label');
        if (labelElement) {
          labelElement.textContent = yardText;
        }

        console.log('[Queue] Reverted display to', previousUnits, 'units');
      }
    }

    /**
     * Show error message
     */
    showErrorMessage(yardsDisplayElement, message) {
      // Remove existing error
      const existingError = yardsDisplayElement.closest('.boost-yards-wrapper').querySelector('.boost-yardage-error');
      if (existingError) {
        existingError.remove();
      }

      // Create error tooltip
      const error = document.createElement('div');
      error.className = 'boost-yardage-error';
      error.style.cssText = 'position: absolute; background: #d9534f; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; margin-top: 4px; z-index: 1000;';
      error.textContent = message;

      yardsDisplayElement.closest('.boost-yards-wrapper').appendChild(error);

      // Auto-remove after 3 seconds
      setTimeout(() => error.remove(), 3000);
    }

    /**
     * Update Boost cart totals
     */
    updateBoostCartTotals(cart) {
      // Update cart count badge
      const cartCount = document.querySelector('.boost-sd__cart-count, [data-cart-count]');
      if (cartCount) {
        cartCount.textContent = cart.item_count;
      }

      // Update subtotal
      const subtotal = document.querySelector('.boost-sd__cart-subtotal, [data-cart-subtotal]');
      if (subtotal) {
        subtotal.textContent = this.formatMoney(cart.total_price);
      }

      console.log('[Queue] Updated cart totals: count =', cart.item_count, 'subtotal =', this.formatMoney(cart.total_price));
    }

    /**
     * Format money
     */
    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    /**
     * Delay helper
     */
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }

  // Initialize queue
  const cartQueue = new CartRequestQueue();

  // Expose to window for debugging
  window.cartQueue = cartQueue;

  // Cache cart data for display conversion
  let cartData = null;

  /**
   * Fetch cart data from Shopify API
   */
  async function fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      cartData = await response.json();
      console.log('[Boost Yardage] Fetched cart data:', cartData);
      return cartData;
    } catch (error) {
      console.error('[Boost Yardage] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Check if item has Yardage property by index
   */
  function hasYardageProperty(item, index) {
    if (!cartData || !cartData.items || !cartData.items[index]) {
      return false;
    }

    const cartItem = cartData.items[index];
    const hasYardage = cartItem.properties && cartItem.properties.Yardage != null && cartItem.properties.Yardage !== '';

    console.log('[Boost Yardage] Item', index, '-', cartItem.product_title, '- has Yardage:', hasYardage);

    return hasYardage;
  }

  /**
   * Extract variant ID from Boost cart item
   */
  function getVariantId(boostCartItem) {
    // Try data attribute first
    let variantId = boostCartItem.getAttribute('data-variant-id');

    // Fallback: extract from hidden input
    if (!variantId) {
      const variantInput = boostCartItem.querySelector('input[name*="variant"], input[name*="id"]');
      variantId = variantInput?.value;
    }

    // Fallback: extract from product URL
    if (!variantId) {
      const productLink = boostCartItem.querySelector('a[href*="/products/"]');
      if (productLink) {
        const urlParams = new URLSearchParams(productLink.search);
        variantId = urlParams.get('variant');
      }
    }

    // Final fallback: use cart data to match by index
    if (!variantId && cartData) {
      const cartItems = document.querySelectorAll('.boost-sd__cart-item');
      const index = Array.from(cartItems).indexOf(boostCartItem);
      if (index >= 0 && cartData.items[index]) {
        variantId = String(cartData.items[index].variant_id);
        console.log('[Boost Yardage] Matched variant ID by index:', variantId);
      }
    }

    if (!variantId) {
      console.error('[Boost Yardage] Could not extract variant ID from:', boostCartItem);
    }

    return variantId;
  }

  /**
   * Update yardage display optimistically
   */
  function updateYardageDisplay(yardsDisplayElement, units) {
    if (!yardsDisplayElement) {
      console.error('[Boost Yardage] updateYardageDisplay called with null element');
      return;
    }

    const yards = units / CONFIG.yardsToUnitsMultiplier;
    const yardText = yards === 1 ? 'yard' : 'yards';

    // Store previous units for revert
    const currentUnits = parseInt(yardsDisplayElement.getAttribute('data-units')) || 0;
    yardsDisplayElement.setAttribute('data-previous-units', currentUnits);

    // Update the number
    yardsDisplayElement.textContent = yards;

    // Update the label
    const labelElement = yardsDisplayElement.parentElement.querySelector('.boost-yards-label');
    if (labelElement) {
      labelElement.textContent = yardText;
    }

    // Store new units
    yardsDisplayElement.setAttribute('data-units', units);
  }

  /**
   * Setup queued +/- buttons for a cart item
   */
  function setupQueuedButtons(boostCartItem) {
    // Check if this is a fractional yardage item
    const yardsDisplay = boostCartItem.querySelector('.boost-yards-display');
    if (!yardsDisplay) return; // Not a fractional item

    const variantId = getVariantId(boostCartItem);
    if (!variantId) return;

    const quantityInput = boostCartItem.querySelector('.boost-sd__quantity-input');
    if (!quantityInput) return;

    // Find +/- buttons
    let minusBtn = boostCartItem.querySelector('button[data-action="minus"], .boost-sd__quantity-btn--minus, button:has([aria-label*="Decrease"])');
    let plusBtn = boostCartItem.querySelector('button[data-action="plus"], .boost-sd__quantity-btn--plus, button:has([aria-label*="Increase"])');

    if (!minusBtn || !plusBtn) {
      // Fallback: find all buttons in quantity container
      const quantityContainer = quantityInput.closest('.boost-sd__quantity-selector, .boost-sd__quantity-wrapper');
      if (quantityContainer) {
        const allButtons = quantityContainer.querySelectorAll('button');
        if (allButtons.length >= 2) {
          minusBtn = allButtons[0];
          plusBtn = allButtons[allButtons.length - 1];
        }
      }
    }

    if (!minusBtn || !plusBtn) {
      console.error('[Boost Yardage] Could not find +/- buttons for variant', variantId);
      return;
    }

    // Don't re-initialize
    if (minusBtn.hasAttribute('data-queue-initialized')) return;
    minusBtn.setAttribute('data-queue-initialized', 'true');
    plusBtn.setAttribute('data-queue-initialized', 'true');

    console.log('[Boost Yardage] Setting up queued buttons for variant', variantId);

    // Minus button handler
    minusBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const currentUnits = parseInt(quantityInput.value) || 0;
      const newUnits = Math.max(1, currentUnits - 1); // Min 0.5 yards = 1 unit

      if (newUnits !== currentUnits) {
        // Optimistic update
        updateYardageDisplay(yardsDisplay, newUnits);
        quantityInput.value = newUnits;

        // Queue for API update
        cartQueue.enqueue(variantId, newUnits, yardsDisplay);
      }
    });

    // Plus button handler
    plusBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const currentUnits = parseInt(quantityInput.value) || 0;
      const newUnits = currentUnits + 1;

      // Optimistic update
      updateYardageDisplay(yardsDisplay, newUnits);
      quantityInput.value = newUnits;

      // Queue for API update
      cartQueue.enqueue(variantId, newUnits, yardsDisplay);
    });

    console.log('[Boost Yardage] Queue buttons initialized for variant', variantId);
  }

  /**
   * Convert a cart item's quantity display to yards
   */
  async function convertItemToYards(item, index) {
    const quantityInput = item.querySelector('.boost-sd__quantity-input');

    if (!quantityInput || quantityInput.hasAttribute('data-yards-converted')) {
      return; // Already converted or not found
    }

    // ONLY convert if this item has a Yardage property
    if (!hasYardageProperty(item, index)) {
      return;
    }

    // CRITICAL: Verify product actually has "Fractional Yardage" tag before displaying as yards
    if (!cartData || !cartData.items || !cartData.items[index]) {
      return;
    }

    const cartItem = cartData.items[index];
    const productHandle = cartItem.handle;
    const isFractional = await checkProductTags(productHandle, cartItem);

    if (!isFractional) {
      console.warn('[Boost Yardage] ⚠️ Product', productHandle, 'has Yardage property but NO Fractional Yardage tag. Skipping conversion.');
      return; // Don't convert display for non-fractional products
    }

    console.log('[Boost Yardage] ✓ Product', productHandle, 'IS fractional. Converting display to yards.');

    const units = parseInt(quantityInput.value) || 0;
    const yards = units / CONFIG.yardsToUnitsMultiplier;
    const yardText = yards === 1 ? 'yard' : 'yards';

    // Mark as converted
    quantityInput.setAttribute('data-yards-converted', 'true');
    quantityInput.setAttribute('data-original-value', units);

    // Create wrapper for number + label
    const yardsWrapper = document.createElement('div');
    yardsWrapper.className = 'boost-yards-wrapper';
    yardsWrapper.style.cssText = 'display: inline-flex; flex-direction: column; align-items: center; position: relative;';

    // Create yards display
    const yardsDisplay = document.createElement('div');
    yardsDisplay.className = 'boost-yards-display';
    yardsDisplay.style.cssText = 'display: flex; align-items: center; justify-content: center; min-width: 50px; height: 32px; font-weight: 500; font-size: 14px;';
    yardsDisplay.textContent = yards;
    yardsDisplay.setAttribute('data-units', units);

    // Create "yards" text
    const yardsLabel = document.createElement('div');
    yardsLabel.className = 'boost-yards-label';
    yardsLabel.style.cssText = 'font-size: 11px; color: #666; margin-top: 6px; white-space: nowrap;';
    yardsLabel.textContent = yardText;

    // Add to wrapper
    yardsWrapper.appendChild(yardsDisplay);
    yardsWrapper.appendChild(yardsLabel);

    // Hide the original input
    quantityInput.style.display = 'none';

    // Insert wrapper
    quantityInput.parentNode.insertBefore(yardsWrapper, quantityInput.nextSibling);

    // Convert price to per-yard display
    const priceWrapper = item.querySelector('.boost-sd__cart-item-quantity-price');
    if (priceWrapper && !priceWrapper.hasAttribute('data-price-converted')) {
      const priceText = priceWrapper.textContent;
      const priceMatch = priceText.match(/\$?([\d,]+\.?\d*)/);

      if (priceMatch) {
        const unitPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
        const yardPrice = unitPrice * CONFIG.yardsToUnitsMultiplier;

        priceWrapper.innerHTML = `
          <span style="margin-right: 4px;">×</span>
          <span>$${yardPrice.toFixed(2)}/yard</span>
        `;

        priceWrapper.style.cssText = 'display: flex; align-items: center; font-size: 13px; white-space: nowrap; flex-wrap: nowrap;';
        priceWrapper.setAttribute('data-price-converted', 'true');
        priceWrapper.setAttribute('data-original-price', unitPrice);

        console.log('[Boost Yardage] Converted price from $' + unitPrice + ' to $' + yardPrice.toFixed(2) + '/yard');
      }
    }

    console.log('[Boost Yardage] Converted', units, 'units to', yards, 'yards');

    // Setup queued buttons for this item (THIS ENABLES +/- BUTTONS!)
    setupQueuedButtons(item);
  }

  // Global lock: prevents concurrent convertBoostCart() runs that create duplicate wrappers
  // Race condition: both calls pass `data-yards-converted` check before either sets it (async gap during checkProductTags)
  let isConverting = false;

  /**
   * Convert all items in the Boost cart
   */
  async function convertBoostCart() {
    if (isConverting) {
      return;
    }
    isConverting = true;

    try {
      const cartItems = document.querySelectorAll('.boost-sd__cart-item');

      if (cartItems.length === 0) return;

      console.log('[Boost Yardage] Found', cartItems.length, 'cart items');

      // Fetch cart data first
      await fetchCartData();

      // Convert items in parallel for faster rendering
      await Promise.all(
        Array.from(cartItems).map((item, index) => convertItemToYards(item, index))
      );
    } finally {
      isConverting = false;
    }
  }

  /**
   * Watch for Boost cart to open/update
   */
  function watchBoostCart() {
    let debounceTimer = null;

    const observer = new MutationObserver(function(mutations) {
      // Check if Boost cart is visible
      const boostCart = document.querySelector('.boost-sd__modal[aria-modal="true"]');

      if (boostCart && boostCart.style.display !== 'none') {
        // Debounce: Boost fires 10-20 mutations per open — coalesce into one conversion call
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(convertBoostCart, 50);
      }
    });

    // Observe the body
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', 'style']
    });

    // Try converting immediately
    convertBoostCart();

    console.log('[Boost Yardage] Monitoring for Boost cart...');
  }

  /**
   * Refresh cart display after external changes
   * Called when cart-property-interceptor dispatches 'cart:updated'
   */
  async function refreshBoostCartDisplay() {
    console.log('[Boost Yardage] Refreshing display after cart update');

    // Invalidate cart cache
    cartData = null;

    // Wait for Boost to finish its refresh
    await new Promise(resolve => setTimeout(resolve, 200));

    // Fetch fresh cart data
    await fetchCartData();

    // Update displays
    const cartItems = document.querySelectorAll('.boost-sd__cart-item');
    cartItems.forEach((item, index) => {
      if (!hasYardageProperty(item, index)) {
        return;
      }

      const quantityInput = item.querySelector('.boost-sd__quantity-input');
      const yardsDisplay = item.querySelector('.boost-yards-display');
      const yardsLabel = item.querySelector('.boost-yards-label');

      if (quantityInput && yardsDisplay) {
        const units = parseInt(quantityInput.value) || 0;
        const yards = units / CONFIG.yardsToUnitsMultiplier;
        const yardText = yards === 1 ? 'yard' : 'yards';

        yardsDisplay.textContent = yards;
        yardsDisplay.setAttribute('data-units', units);

        if (yardsLabel) {
          yardsLabel.textContent = yardText;
        }

        console.log('[Boost Yardage] Refreshed display for item', index, 'to', yards, yardText);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBoostCart);
  } else {
    watchBoostCart();
  }

  // Listen for cart updates from interceptor
  document.addEventListener('cart:updated', function() {
    console.log('[Boost Yardage] Heard cart:updated event, refreshing display');
    refreshBoostCartDisplay();
  });

  console.log('[Boost Yardage] Event listener registered for cart:updated');

  // Debug helpers
  window.boostCartDebug = {
    queue: cartQueue,
    checkButtons: function() {
      const boostCart = document.querySelector('.boost-sd__cart');
      if (!boostCart) {
        console.log('❌ Boost cart not open');
        return;
      }
      const buttons = boostCart.querySelectorAll('button');
      const visible = Array.from(buttons).filter(b => window.getComputedStyle(b).display !== 'none');
      console.log(`✅ ${visible.length} / ${buttons.length} buttons visible`);
      return { total: buttons.length, visible: visible.length };
    },
    showQueueStatus: function() {
      console.log('Queue Status:', {
        processing: cartQueue.processing,
        queueSize: cartQueue.queue.size,
        cacheExpiry: new Date(cartQueue.cartCacheExpiry).toLocaleTimeString()
      });
    }
  };

  console.log('[Boost Yardage] CartRequestQueue loaded - +/- buttons work!');

})();
